// LA BARRE TECHNIQUE — ce qu'on regarde en réglant.
//
// BPM et SA SOURCE, dansabilité et d'où elle vient, état de la recherche
// radiopi, points de mixage, phase du morceau. Cent quatre-vingts lignes qui
// vivaient au milieu des deux mille cinq cents de main-radio.js.
//
// SORTIES plutôt que copiées : l'écran de diffusion et judicael.com doivent
// afficher les mêmes chiffres, et deux copies divergent au premier réglage.
//
// Le partage se fait en deux temps parce que les fonctions n'ont pas toutes
// le même besoin. Les formateurs ne lisent QUE le profil : ils s'exportent
// tels quels, et comme un import est hissé, main-radio.js peut les appeler
// depuis n'importe où sans se soucier de l'ordre. Les quatre qui écrivent
// dans le DOM, ou qui consultent l'état webradiopi, passent par une fabrique
// à qui on donne ces éléments une fois.
import { radioLatencySeconds } from './soloist-visual-profile.js';

const RADIO_LATENCY_S = radioLatencySeconds();

const MIX_DROPS_SHOWN = 3;

const WEBRADIO_STATE_LABELS = {
  awaiting_recording: 'webradiopi: 1ère lecture, en attente de l’enregistrement',
  recording: 'webradiopi: 1ère lecture, ENREGISTREMENT en cours…',
  uploaded: 'webradiopi: 1ère lecture, envoi de l’enregistrement…',
  analyzing: 'webradiopi: 1ère lecture, analyse de l’enregistrement…',
  skipped: 'webradiopi: 1ère lecture, début manqué (enregistrement au prochain passage)',
  failed: 'webradiopi: analyse de l’enregistrement échouée',
  cached: 'analyse: cache',
  analyzed: 'analyse: radiopi',
  processing: 'analyse: radiopi en cours (première lecture)…',
  unavailable: 'analyse: impossible sans durée (estimation du modèle seule)',
  error: 'analyse: échec'
};

const LYRICS_OFFSET_BY_SOURCE = { direct: 1.0, stream: -1.0 };

const LYRICS_OFFSET_OVERRIDE = (function () {
  const raw = new URLSearchParams(location.search).get('lyricsoffset');
  if (raw == null) return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
})();

export function fmtClock(ms) {
  const totalSeconds = Math.floor(ms / 1000);
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return m + ':' + (s < 10 ? '0' : '') + s;
}

export function fmtTimingSegment(profile) {
  const posClock = fmtClock(currentPositionSeconds(profile) * 1000);
  return profile.duration > 0 ? posClock + ' / ' + fmtClock(profile.duration * 1000) : posClock;
}

export function currentPositionSeconds(profile) {
  return profile.estimatedPosition != null ? profile.estimatedPosition : profile.position;
}

export function lyricsOffsetSeconds(profile) {
  if (LYRICS_OFFSET_OVERRIDE != null) return LYRICS_OFFSET_OVERRIDE;
  const offset = LYRICS_OFFSET_BY_SOURCE[profile && profile.source];
  return offset != null ? offset : 0;
}

export function fmtLyricSegment(profile) {
  if (!profile.lyricsResolved) return '';
  if (!profile.lyricsFound) return 'paroles introuvables';
  if (profile.lyricHooks && profile.lyricHooks.length > 0) {
    return 'paroles: ' + profile.lyricHooks.map(function (h) { return h.phrase; }).join(' / ');
  }
  return '';
}

export function fmtPhaseSegment(profile) {
  if (!profile.analysisTimeline) return 'phase —';
  const mode = profile.analysisTimeline.lookupMode(currentPositionSeconds(profile) * 1000);
  return 'phase ' + (mode || '—');
}

export function fmtApiStatusSegment(profile) {
  switch (profile.apiStatus) {
    case 'quick': return 'API: recherche TheAudioDB…';
    case 'quick-ok': return 'API: TheAudioDB (webradiopi en cours…)';
    case 'analyzed': return 'API: webradiopi';
    // État TERMINAL distinct de 'failed' : TheAudioDB a bien répondu, c'est
    // radiopi seul qui n'aboutira pas - typiquement un NOT_FOUND, aucune
    // version du morceau n'ayant une durée compatible sur YouTube. Sans lui,
    // le libellé restait « radiopi en cours… » indéfiniment et laissait croire
    // à une recherche interminable alors que le service avait déjà renoncé.
    case 'quick-only': return 'API: TheAudioDB (webradiopi sans résultat)';
    case 'failed': return 'API: indisponible';
    default: return '';
  }
}

export function fmtMixPointsSegment(profile) {
  const analysis = profile.trackAnalysis;
  const cue = analysis && analysis.structure && analysis.structure.cue_points;
  const parts = [];
  if (cue) {
    if (cue.mix_in_ms != null) parts.push('in ' + fmtClock(cue.mix_in_ms));
    if (cue.mix_out_ms != null) parts.push('out ' + fmtClock(cue.mix_out_ms));
    const drops = cue.drops || [];
    if (drops.length > 0) {
      parts.push('drops ' + drops.slice(0, MIX_DROPS_SHOWN)
        .map(function (d) { return fmtClock(d.time_ms); }).join(' '));
      if (drops.length > MIX_DROPS_SHOWN) parts.push('+' + (drops.length - MIX_DROPS_SHOWN));
    }
  }
  if (profile.analysisTimeline) {
    const nowMs = currentPositionSeconds(profile) * 1000;
    const next = profile.analysisTimeline.marks.find(function (m) { return m.atMs > nowMs; });
    if (next) {
      parts.push('next ' + next.type + ' dans ' + Math.round((next.atMs - nowMs) / 1000) + 's');
    }
  }
  return parts.length ? 'mix: ' + parts.join(' · ') : 'mix —';
}

export function fmtLyricsSourcePanel(profile) {
  const src = profile.lyricsSource;
  const q = profile.lyricsQuery;
  if (!src) {
    if (!q) return '';
    // Resolved, and nothing matched. What was SEARCHED for is the useful half
    // of that: cleanTitle/cleanArtist strip a lot, and a miss is often about
    // what got stripped rather than about LRCLIB not having the song.
    return 'lrc: aucun\ncherché "' + q.title + '"\n        / "' + q.artist + '"';
  }
  const dur = src.duration != null ? Math.round(src.duration) + 's' : '—';
  // One field per line: it sits in a corner now, not inline in a bar, so it
  // can afford to be read rather than scanned.
  return 'lrc #' + (src.id != null ? src.id : '?')
    + (q && q.attempt != null ? '   q' + q.attempt : '') + '\n'
    + '"' + src.trackName + '"\n'
    + src.artistName + '\n'
    + (src.albumName ? src.albumName + '\n' : '')
    + dur;
}

// Les quatre qui ont besoin d'un contexte : trois écrivent dans le DOM, la
// quatrième lit la carte des états webradiopi tenue par l'appelant.
export function creerBarreTechnique(cfg) {
  const elTrackFeaturesBar = cfg.barEl;
  const elDanceDebug = cfg.danceEl || null;
  const elLyricsSourceDebug = cfg.lyricsEl || null;
  // Sur l'écran de diffusion personne ne tient cette carte ; une Map vide
  // fait disparaître le segment d'elle-même plutôt que de lever.
  const webradioStateByTrack = cfg.webradioStateByTrack || new Map();

  function fmtWebradioSegment(profile) {
    const state = webradioStateByTrack.get(profile.trackId);
    let text = state ? (WEBRADIO_STATE_LABELS[state] || ('webradiopi: ' + state)) : '';
    // A cached analysis that came from a Pi recording says how much of the
    // head was missed (Spotify's crossfade starts the track before Soloist
    // announces it) - that stretch is silence in the analysed file, so no
    // drop/onset can come from it. Timings elsewhere are the real track's.
    const track = profile.trackAnalysis && profile.trackAnalysis.track;
    if (track && track.source === 'webradio-recording' && track.recorded_offset_ms > 0) {
      text += (text ? ' ' : 'webradiopi: cache ') + '(enreg. dès +' + (track.recorded_offset_ms / 1000).toFixed(1) + 's)';
    }
    return text;
  }

  function renderLyricsSourceDebug(profile) {
    elLyricsSourceDebug.textContent = profile ? fmtLyricsSourcePanel(profile) : '';
  }

  function renderDanceDebug(profile) {
    if (!elDanceDebug) return;
    const f = profile.trackFeatures;
    const d = profile.danceability;
  
    const deezer = f && f.danceability != null ? f.danceability / 100 : null;
    const llm = d && d.llmValue != null ? d.llmValue : null;
    // Only a real measurement goes on the radiopi row. While the estimate is
    // still standing, `value` IS the llm figure, and copying it down here would
    // show two identical numbers and imply the audio had been analysed.
    const signal = d && d.source === 'signal' ? d.value : null;
  
    const live = !d ? null : (d.source === 'signal' ? 'radiopi' : d.source === 'llm' ? 'llm' : null);
    // Une note que le modèle a lui-même désavouée - familiarité trop basse -
    // est affichée QUAND MÊME, mais marquée. Sans cette marque, la ligne « llm
    // 0.70 » à côté d'un « radiopi 0.35 » retenu passe pour une incohérence :
    // on lit deux nombres et la règle du plus haut des deux semble ignorée,
    // alors que l'un des deux a été écarté à la source.
    const rejete = !!(d && d.llmOk === false && d.llmValue != null);
    const row = function (label, value, isLive, note) {
      const shown = value == null ? '  —  ' : value.toFixed(2);
      return '  ' + label.padEnd(8) + shown + (isLive ? '  <' : '') + (note ? '  ' + note : '');
    };
  
    const lines = [
      'DANSABILITÉ',
      row('deezer', deezer, false),
      row('llm', llm, live === 'llm', rejete ? '(écarté : inconnu du modèle)' : ''),
      row('radiopi', signal, live === 'radiopi')
    ];
    // Why the client is on the value it is on - "défaut" is the case worth
    // seeing, since it means neither source produced anything usable and the
    // 0.5 in play is a floor, not a reading.
    if (d && d.source === 'default') {
      lines.push('  (défaut 0.50 — ' + (d.reason || 'aucune source') + ')');
    }
    lines.push('');
    lines.push('  energy  ' + (f && f.energy != null ? f.energy : '—')
      + '   valence ' + (f && f.valence != null ? f.valence : '—'));
    lines.push('  mood    ' + (f && f.mood ? f.mood : '—'));
    elDanceDebug.textContent = lines.join('\n');
  }

  function renderTrackFeaturesBar(profile) {
    // Refreshed together with the bar - same data, same cadence, just no longer
    // sharing its line.
    renderLyricsSourceDebug(profile);
    if (!profile.title) {
      elTrackFeaturesBar.textContent = '';
      // The box clears with the bar. Left alone it would keep showing the
      // previous track's three figures against an empty stage.
      if (elDanceDebug) elDanceDebug.textContent = '';
      return;
    }
    const f = profile.trackFeatures;
    const fmt = function (value, suffix) {
      return value == null ? '—' : value + (suffix || '');
    };
    const lyricSegment = fmtLyricSegment(profile);
    // Printed so a wrong source guess is VISIBLE. The offset applied to the
    // lyrics depends entirely on this classification, and the AirPlay URI
    // prefix behind it could not be confirmed against a live AirPlay stream -
    // if this ever reads "direct" while AirPlaying, that is the bug, and the
    // number next to it is the correction being wrongly applied.
    // RADIO: the stream latency being subtracted from the position is the
    // number worth seeing here, next to the lyrics offset it replaces.
    const sourceSegment = 'src ' + (profile.source || '—')
      + ' latence -' + (typeof window !== 'undefined' && Number.isFinite(window.__latenceRadio) ? window.__latenceRadio : RADIO_LATENCY_S).toFixed(1) + 's'
      + ' lrc ' + (lyricsOffsetSeconds(profile) >= 0 ? '+' : '')
      + lyricsOffsetSeconds(profile).toFixed(1) + 's';
    const apiStatusSegment = [fmtWebradioSegment(profile), fmtApiStatusSegment(profile)].filter(Boolean).join('  ·  ');
    // The bottom line is now about the TIMELINE - tempo and the structure the
    // analysis found in the track. The danceability figures and Deezer's other
    // character fields moved to their own box (renderDanceDebug); what is left
    // here all answers one question, "where are we in this track and what
    // happens next", which is what makes it readable at a glance again.
    elTrackFeaturesBar.textContent =
      profile.title + ' · ' + profile.artist +
      '  ·  ' + fmtTimingSegment(profile) +
      '  |  BPM ' + fmt(f && f.bpm) + (f && f.bpm != null && f.bpmSource ? ' (' + f.bpmSource + ')' : '') +
      '  ·  ' + fmtPhaseSegment(profile) +
      '  ·  ' + fmtMixPointsSegment(profile) +
      '  ·  ' + sourceSegment +
      (lyricSegment ? '  ·  ' + lyricSegment : '') +
      (apiStatusSegment ? '  ·  ' + apiStatusSegment : '');
    renderDanceDebug(profile);
  }

  return {
    render: renderTrackFeaturesBar,
    renderDance: renderDanceDebug,
    renderLyricsSource: renderLyricsSourceDebug
  };
}
